// src/pages/AuthCallback.tsx
import { IonContent, IonPage, IonSpinner, IonText } from "@ionic/react";
import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { supabase } from "../lib/supabaseClient.ts";

const API_URL = process.env.REACT_APP_API_URL || 'https://api.careertwins.com/api/v1';

async function exchangeSupabaseToken(supabaseToken: string): Promise<string | null> {
  try {
    const response = await fetch(`${API_URL}/callback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ supabase_token: supabaseToken }),
    });
    if (!response.ok) {
      console.error('🔐 Backend callback failed:', response.status, await response.text());
      return null;
    }
    const data = await response.json();
    return data.access_token || null;
  } catch (err) {
    console.error('🔐 Backend callback error:', err);
    return null;
  }
}

const AuthCallback: React.FC = () => {
  const history = useHistory();
  const [status, setStatus] = useState('Processing login...');

  useEffect(() => {
    let active = true;

    // Timeout fallback in case auth state never fires
    const timeout = setTimeout(() => {
      if (active) {
        console.log('🔐 Auth callback timeout - no session established');
        setStatus('Authentication timed out. Redirecting...');
        history.replace('/login');
      }
    }, 10000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (!active) return;

      console.log('🔐 Auth state change:', event, { hasSession: !!session });

      if ((event === 'SIGNED_IN' || event === 'INITIAL_SESSION') && session) {
        clearTimeout(timeout);
        try {
          setStatus('Verifying authentication...');
          const userId = session.user.id;
          console.log('🔐 Login successful, user ID:', userId);

          // Exchange Supabase token for backend app token
          const appToken = await exchangeSupabaseToken(session.access_token);
          if (appToken) {
            localStorage.setItem('authToken', appToken);
            localStorage.setItem('authTokenType', 'Bearer');
            console.log('🔐 Backend token stored');
          } else {
            console.warn('🔐 Could not get backend token, falling back to Supabase token');
          }

          // Clear old user's cache on new login
          const cachedUserId = localStorage.getItem('cached_user_id');
          if (cachedUserId && cachedUserId !== userId) {
            console.log('🔐 Clearing cache from previous user');
            localStorage.removeItem(`profile_resumes_${cachedUserId}`);
            localStorage.removeItem(`profile_analysis_${cachedUserId}`);
            localStorage.removeItem('profile_resumes');
            localStorage.removeItem('profile_analysis');
          }

          localStorage.setItem('cached_user_id', userId);

          setStatus('Login successful! Redirecting...');
          await new Promise(r => setTimeout(r, 500));

          if (active) {
            console.log('🔐 Redirecting to profile');
            history.replace('/app/profile');
          }
        } catch (err) {
          console.error('🔐 OAuth callback error:', err);
          setStatus('An error occurred. Redirecting...');
          await new Promise(r => setTimeout(r, 1500));
          if (active) history.replace('/login');
        }
      } else if (event === 'SIGNED_OUT' || (event === 'INITIAL_SESSION' && !session)) {
        // Only redirect on explicit sign-out or confirmed no session
        // Don't redirect on INITIAL_SESSION with no session — wait for SIGNED_IN
      }
    });

    return () => {
      active = false;
      clearTimeout(timeout);
      subscription.unsubscribe();
    };
  }, [history]);

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div className="flex flex-col items-center justify-center min-h-screen">
          <IonSpinner name="crescent" />
          <IonText color="medium" className="mt-4">
            {status}
          </IonText>
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AuthCallback;
